<script lang="ts">
	import type { Snippet } from 'svelte';
	import Spinner from './Spinner.svelte';

	type Variant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'success';
	type Size = 'small' | 'medium' | 'large';

	interface Props {
		variant?: Variant;
		size?: Size;
		/** Растянуть на всю доступную ширину — для форм и модальных окон. */
		block?: boolean;
		disabled?: boolean;
		/** Показывает индикатор и блокирует повторные нажатия. */
		loading?: boolean;
		type?: 'button' | 'submit' | 'reset';
		href?: string;
		title?: string;
		class?: string;
		onclick?: (event: MouseEvent) => void;
		children: Snippet;
	}

	let {
		variant = 'primary',
		size = 'medium',
		block = false,
		disabled = false,
		loading = false,
		type = 'button',
		href,
		title,
		class: className = '',
		onclick,
		children
	}: Props = $props();

	// Во время загрузки кнопка неактивна: иначе повторное нажатие отправит
	// запрос дважды — в мессенджере это дубль сообщения или второй звонок.
	let isDisabled = $derived(disabled || loading);
</script>

{#snippet content()}
	{#if loading}
		<Spinner size={size === 'small' ? 14 : 18} />
	{/if}
	<span class="label" class:hidden={loading}>{@render children()}</span>
{/snippet}

{#if href && !isDisabled}
	<a class="button {variant} {size} {className}" class:block class:loading {href} {title} {onclick}>
		{@render content()}
	</a>
{:else}
	<button
		class="button {variant} {size} {className}"
		class:block
		class:loading
		{type}
		{title}
		disabled={isDisabled}
		{onclick}
	>
		{@render content()}
	</button>
{/if}

<style>
	.button {
		position: relative;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: var(--spacing-s);
		border-radius: var(--radius-button-medium);
		font: var(--text-button);
		white-space: nowrap;
		text-decoration: none;
		transition:
			background var(--duration-fast) var(--ease-standard),
			color var(--duration-fast) var(--ease-standard),
			opacity var(--duration-fast) var(--ease-standard);
	}

	.button:hover {
		text-decoration: none;
	}

	.button:disabled {
		opacity: 0.45;
	}

	/* Лёгкое «вдавливание» при нажатии — тактильный отклик без анимации. */
	.button:not(:disabled):active {
		transform: scale(0.985);
	}

	/* --- Размеры --------------------------------------------------------- */
	.small {
		height: var(--size-32);
		padding: 0 var(--spacing-m);
		border-radius: var(--radius-button-small);
		font-size: var(--font-size-m);
	}

	.medium {
		height: var(--size-40);
		padding: 0 var(--spacing-l);
	}

	.large {
		height: var(--size-52);
		padding: 0 var(--spacing-2xl);
		border-radius: var(--radius-button-large);
	}

	.block {
		display: flex;
		width: 100%;
	}

	/* --- Виды ------------------------------------------------------------ */
	.primary {
		background: var(--accent-primary);
		color: var(--text-on-accent);
	}

	.primary:not(:disabled):hover {
		background: var(--accent-hover);
	}

	.primary:not(:disabled):active {
		background: var(--accent-active);
	}

	.secondary {
		background: var(--background-secondary);
		color: var(--text-primary);
	}

	.secondary:not(:disabled):hover {
		background: var(--background-active);
	}

	.ghost {
		background: transparent;
		color: var(--accent-primary);
	}

	.ghost:not(:disabled):hover {
		background: var(--accent-muted);
	}

	.danger {
		background: var(--status-danger);
		color: #fff;
	}

	.danger:not(:disabled):hover {
		background: var(--status-danger-hover);
	}

	.success {
		background: var(--status-success);
		color: var(--text-on-success);
	}

	.success:not(:disabled):hover {
		background: color-mix(in srgb, var(--status-success) 88%, var(--text-primary));
	}

	/* Подпись прячется, но продолжает занимать место — кнопка не «прыгает»
	   в момент показа индикатора загрузки. */
	.label {
		display: inline-flex;
		align-items: center;
		gap: var(--spacing-s);
	}

	.label.hidden {
		visibility: hidden;
	}

	.loading .label {
		position: absolute;
	}
</style>
